import { Button, Checkbox, Input } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { FilterChip } from '@/design';
import { GROUPS } from '../mail-templates/template-picker';
import { useEveryTemplate } from '../mail-templates/template-nav';

/**
 * The Email log's toolbar filters (SR-523 stage 6) that its columns cannot carry: several
 * templates or tags at once — any of them — and a date range. Each is a quick filter chip of the
 * toolbar; the values go to the address as repeated parameters (`template=a&template=b`).
 */

export interface IMultiOption {
  value: string;
  label: string;
  /** The heading it is listed under; none lists it ungrouped. */
  group?: string;
  /** Said muted after it ("archived"). */
  detail?: string;
}

/** Past this many options, a box narrows them. */
const SEARCH_FROM = 8;

/** Several values of one vocabulary, any of them: ticked in the chip's popover. A value the
 *  address names that is not among the options is listed as spelled. */
export function MultiChip({
  label,
  values,
  options,
  onChange,
  empty = 'Nothing to choose from.'
}: {
  label: string;
  values: readonly string[];
  options: readonly IMultiOption[];
  onChange: (values: string[]) => void;
  empty?: string;
}) {
  const id = React.useId();
  const [find, setFind] = React.useState('');
  const known = new Set(options.map((option) => option.value.toLowerCase()));
  const all: IMultiOption[] = [
    ...options,
    ...values.filter((value) => !known.has(value.toLowerCase())).map((value) => ({ value, label: value }))
  ];
  const labelOf = (value: string) =>
    all.find((option) => option.value.toLowerCase() === value.toLowerCase())?.label ?? value;
  const wanted = find.trim().toLowerCase();
  const shown = wanted ? all.filter((option) => option.label.toLowerCase().includes(wanted)) : all;
  const groups = [...new Set(shown.map((option) => option.group ?? ''))];
  const checked = (value: string) => values.some((each) => each.toLowerCase() === value.toLowerCase());
  const toggle = (value: string, on: boolean) =>
    onChange(on ? [...values, value] : values.filter((each) => each.toLowerCase() !== value.toLowerCase()));

  return (
    <FilterChip label={label} summary={values.map(labelOf).join(', ')} className="w-80">
      {all.length > SEARCH_FROM && (
        <Input
          value={find}
          onChange={(event) => setFind(event.target.value)}
          placeholder={`Find among ${all.length}`}
          aria-label={`Find a ${label.toLowerCase()}`}
          className="h-8"
        />
      )}
      {all.length === 0 && <p className="text-muted-foreground">{empty}</p>}
      {all.length > 0 && shown.length === 0 && <p className="text-muted-foreground">Nothing matches.</p>}
      <div className="-mx-1 grid max-h-72 gap-2 overflow-y-auto px-1" data-multi-filter={label}>
        {groups.map((group) => (
          <fieldset key={group} className="grid gap-1" aria-label={group || label}>
            {group && <legend className="mb-0.5 text-xs font-medium text-muted-foreground">{group}</legend>}
            {shown
              .filter((option) => (option.group ?? '') === group)
              .map((option) => (
                <label
                  key={option.value}
                  className="flex min-w-0 items-center gap-2"
                  data-multi-option={option.value}
                >
                  <Checkbox
                    checked={checked(option.value)}
                    onCheckedChange={(next) => toggle(option.value, next === true)}
                    aria-labelledby={`${id}-${option.value}`}
                  />
                  <span id={`${id}-${option.value}`} className="truncate">
                    {option.label}
                  </span>
                  {option.detail && <span className="shrink-0 text-xs text-muted-foreground">{option.detail}</span>}
                </label>
              ))}
          </fieldset>
        ))}
      </div>
      {values.length > 0 && (
        <Button type="button" variant="ghost" size="sm" className="justify-self-start" onClick={() => onChange([])}>
          Clear
        </Button>
      )}
    </FilterChip>
  );
}

/** "2026-11-01" — the address's spelling of a day. */
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The instant a day of the address starts, in this browser's zone — `next` the day after's, the
 *  log's `to` being exclusive. A value that is not a day goes as spelled. */
export function dayStart(value: Nullable<string>, next = false): string | undefined {
  if (!value) return undefined;
  if (!DAY.test(value)) return value;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  if (next) date.setDate(date.getDate() + 1);
  return date.toISOString();
}

/** "Nov 1, 2026". */
function dayWords(value: string): string {
  if (!DAY.test(value)) return value;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "Nov 1 – Nov 3, 2026", "From Nov 1, 2026", "Until …", or null for none. */
export function rangeWords(from: Nullable<string>, to: Nullable<string>): Nullable<string> {
  if (from && to) return `${dayWords(from)} – ${dayWords(to)}`;
  if (from) return `From ${dayWords(from)}`;
  if (to) return `Until ${dayWords(to)}`;
  return null;
}

/** The days the mails were handled in, both ends included. */
export function DateChip({
  from,
  to,
  onChange
}: {
  from: Nullable<string>;
  to: Nullable<string>;
  onChange: (from: Nullable<string>, to: Nullable<string>) => void;
}) {
  const id = React.useId();
  const backwards = Boolean(from && to && DAY.test(from) && DAY.test(to) && from > to);
  return (
    <FilterChip label="Date" summary={rangeWords(from, to)}>
      <div className="grid grid-cols-2 gap-2">
        <label htmlFor={`${id}-from`} className="grid gap-1">
          <span className="text-xs text-muted-foreground">From</span>
          <Input
            id={`${id}-from`}
            type="date"
            value={from && DAY.test(from) ? from : ''}
            max={to && DAY.test(to) ? to : undefined}
            onChange={(event) => onChange(event.target.value || null, to)}
            className="h-8"
          />
        </label>
        <label htmlFor={`${id}-to`} className="grid gap-1">
          <span className="text-xs text-muted-foreground">To</span>
          <Input
            id={`${id}-to`}
            type="date"
            value={to && DAY.test(to) ? to : ''}
            min={from && DAY.test(from) ? from : undefined}
            onChange={(event) => onChange(from, event.target.value || null)}
            className="h-8"
          />
        </label>
      </div>
      {backwards && <p className="text-xs text-destructive">The start is after the end: nothing matches.</p>}
      {(from || to) && (
        <Button type="button" variant="ghost" size="sm" className="justify-self-start" onClick={() => onChange(null, null)}>
          Clear
        </Button>
      )}
    </FilterChip>
  );
}

/** Every template the log can name — built-in and custom, the archived too — grouped as the
 *  templates page groups them. A read the caller may not make offers none. */
export function useTemplateChoices(): IMultiOption[] {
  const read = useEveryTemplate();
  return React.useMemo(() => {
    const seen = new Set<string>();
    return GROUPS.flatMap(({ group, heading }) =>
      (read.data ?? [])
        .filter((template) => (template.group ?? 'survey') === group && !seen.has(template.code) && seen.add(template.code))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((template) => ({
          value: template.code,
          label: template.name,
          group: heading,
          detail: template.archivedAt ? 'archived' : undefined
        }))
    );
  }, [read.data]);
}
